/**
 * Strichcodes aus Kamerabildern lesen.
 *
 * Wo der Browser einen eingebauten Leser hat (BarcodeDetector, etwa Chrome
 * auf Android und macOS), wird er genutzt. Sonst übernimmt zxing als
 * WebAssembly; die Datei liefert die App selbst aus, damit nichts von einem
 * fremden Server nachgeladen wird. Sie wird erst beim ersten Scan geladen.
 */
import { isValidEan } from '@cookbook/domain';

/** Formate auf Lebensmitteln und Drogerieartikeln. */
const FORMATS = ['ean_13', 'ean_8', 'upc_a', 'upc_e'] as const;

/** Liest Codes aus einem Videobild. */
export interface CodeReader {
  read(source: HTMLVideoElement): Promise<string[]>;
}

/** Minimale Form des Lesers, wie Browser und zxing ihn anbieten. */
interface Detector {
  detect(source: HTMLVideoElement): Promise<{ rawValue: string }[]>;
}
interface DetectorClass {
  new (options: { formats: string[] }): Detector;
  getSupportedFormats(): Promise<string[]>;
}

/** Bringt einen gelesenen Code in die Form der Datenbanken (UPC-A als EAN-13). */
export function normalizeCode(raw: string): string | null {
  const digits = raw.replace(/\D/g, '');
  const code = digits.length === 12 ? `0${digits}` : digits;
  return isValidEan(code) ? code : null;
}

/** Baut den passenden Leser. */
export async function createCodeReader(): Promise<CodeReader> {
  const native = (window as unknown as { BarcodeDetector?: DetectorClass }).BarcodeDetector;
  let detector: Detector | null = null;
  if (native !== undefined) {
    const supported = await native.getSupportedFormats().catch(() => [] as string[]);
    const formats = FORMATS.filter((format) => supported.includes(format));
    if (formats.length > 0) {
      detector = new native({ formats });
    }
  }
  if (detector === null) {
    const [{ BarcodeDetector, setZXingModuleOverrides }, { default: wasmUrl }] = await Promise.all([
      import('barcode-detector/ponyfill'),
      import('zxing-wasm/reader/zxing_reader.wasm?url'),
    ]);
    setZXingModuleOverrides({
      locateFile: (path: string, prefix: string) => (path.endsWith('.wasm') ? wasmUrl : prefix + path),
    });
    detector = new BarcodeDetector({ formats: [...FORMATS] });
  }
  const reader = detector;
  return {
    async read(source) {
      const results = await reader.detect(source);
      return results.map((result) => result.rawValue);
    },
  };
}
